import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { TransactionForm } from '@/components/forms/TransactionForm';
import { BatchTransactionEntry, OFXUpload } from '@/components/transactions';
import { useCategories } from '@/hooks/useCategories';
import { useCreateTransaction } from '@/hooks/useTransactions';
import { PenLine, List, FileUp, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TransactionFormData } from '@/lib/schemas/forms';

type Mode = 'manual' | 'batch' | 'ofx';

const MODES: { id: Mode; label: string; icon: React.ElementType }[] = [
  { id: 'manual', label: 'Manual', icon: PenLine },
  { id: 'batch', label: 'Em Lote', icon: List },
  { id: 'ofx', label: 'Importar OFX', icon: FileUp },
];

const AddTransactions: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('manual');
  const { data: categories = [] } = useCategories({});
  const createTransaction = useCreateTransaction();

  const handleManualSubmit = (data: TransactionFormData) => {
    createTransaction.mutate(
      {
        amount: parseFloat(data.amount),
        category_id: data.category_id,
        date: data.date,
        description: data.description,
        type: data.type,
      },
      { onSuccess: () => navigate('/transactions') }
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <Link
            to="/transactions"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-3"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Transações
          </Link>
          <PageHeader
            title="Adicionar Transações"
            subtitle="Registre lançamentos manualmente, em lote ou importe um extrato"
          />
        </div>

        {/* Mode switcher */}
        <div className="flex gap-2 flex-wrap">
          {MODES.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setMode(id)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all border',
                mode === id
                  ? 'bg-primary/10 text-primary border-primary/30'
                  : 'bg-muted/50 text-muted-foreground border-border hover:border-primary/25 hover:text-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Active panel */}
        <div key={mode} className="animate-slide-down">
          {mode === 'manual' && (
            <div className="bg-card border border-border rounded-xl p-6">
              <TransactionForm
                categories={categories}
                onSubmit={handleManualSubmit}
                isSubmitting={createTransaction.isPending}
                submitLabel="Registrar transação"
              />
            </div>
          )}

          {mode === 'batch' && (
            <BatchTransactionEntry
              categories={categories}
              onClose={() => navigate('/transactions')}
            />
          )}

          {mode === 'ofx' && <OFXUpload categories={categories} />}
        </div>
      </div>
    </Layout>
  );
};

export default AddTransactions;
